import { decodeDisplayText } from '../../../util/displayText';
import { getAudioWorkspaceSource, getPdfWorkspaceSource } from '../../../util/workspaceNavigation';
import '../../../i18n';
import {useTranslation} from 'react-i18next';
import React, { useState, useEffect, useRef } from 'react';
import ReactPlayer from 'react-player';
import ReactDOM from 'react-dom';
import { prettySize } from '../../../util/fileSizeCalculator';
import openURLFile from '../../../util/openURLFile';

import FileIcon from '../../../appearance/FileIcon';
import ImageIcon from '../../../appearance/ImageIcon';

import {
    GREEN,
    PALE_WHITE_1
} from '../../../appearance/Colors';

import styled from 'styled-components';

import useS3Request from '../../../hooks/useS3Request';
import useCloudinaryRequest from '../../../hooks/useCloudinaryRequest';
import getImagePreviews from '../../../util/getImagePreviews';
import { imageExists } from '../../../util/imageExists';

import FileSaver from 'file-saver';

import { IoMdTrash, IoIosDocument, IoMdOpen, IoMdDownload, IoMdMic } from 'react-icons/io';
import { FiEdit2, FiMoreVertical } from 'react-icons/fi';

import CarouselModal from '../../../components/Modals/CarouselModal';
import DeleteModal from '../../../components/Modals/DeleteModal';
import RenameFileModal from '../../../components/Modals/RenameFileModal';
import AudioPlayer from './AudioPlayer/AudioPlayer';
import useUpdateMessageMutation from '../../../hooks/useUpdateMessageMutation';
import { useMutation, useQuery } from '@apollo/client';

import { QUERY, MUTATION } from '../../../gql-operations';
import { EXTRACT_VIDEO_AUDIO } from '../../../gql-operations/mutations/message/extract-video-audio';
import { GET_FEATURE_FLAG } from '../../../gql-operations/featureFlags';
import useGetMaxFileSize from '../../../util/max_file_size';
import USER_TYPE from '../../../util/userType';
import USER_ROLE from '../../../util/userRole';
import {
    FEATURE_UPGRADE_MODAL_PRESET,
    openFeatureUpgradeModal
} from '../../../util/featureUpgradeModalEvents';

import ActionTag from '../../Action/ActionTagButton';
import LinkTag from '../../Action/LinkTag';
import InactiveTag from '../../Action/InactiveTagButton';

// import FileViewer from 'react-file-viewer';
import { AuthContext } from '../../../util/context';
import useDownloadFile from '../../../hooks/useDownloadFile';
import customAnalytics from '../../../util/custom-analytics';
import {
    evaluateFeatureFlagForUser,
    mapTranscriptionErrorToUiAction
} from '../../../util/featureFlagAccess';
import {
    getApolloErrorMessage,
    getAudioProcessingStatusText,
    isAudioProcessingActive
} from '../../../util/audioProcessingStatus';

const isPdfDocument = (mediaType = '', mediaFormat = '', mediaMime = '') => {
    const normalizedType = String(mediaType || '').toLowerCase();
    const normalizedFormat = String(mediaFormat || '').toLowerCase();
    const normalizedMime = String(mediaMime || '').toLowerCase();

    return (
        normalizedMime === 'application/pdf' ||
        normalizedType === 'application/pdf' ||
        (normalizedType === 'application' && normalizedFormat === 'pdf')
    );
};

const getThumbnailSources = thumbnail => {
    if (!thumbnail) return [];
    if (typeof thumbnail === 'string') return [thumbnail];
    if (typeof thumbnail !== 'object') return [];

    return [
        thumbnail.original,
        thumbnail.xl,
        thumbnail.lg,
        thumbnail.md,
        thumbnail.sm,
        thumbnail.compressed?.original,
        thumbnail.compressed?.xl,
        thumbnail.compressed?.lg,
        thumbnail.compressed?.md,
        thumbnail.compressed?.sm
    ].filter(Boolean);
};

const getUniqueSources = sources => {
    const seen = new Set();
    return sources.filter(source => {
        if (!source || typeof source !== 'string' || seen.has(source)) {
            return false;
        }

        seen.add(source);
        return true;
    });
};

function FilePreviewPlugin(props) {
    return props.readOnly ? <FilePreviewPluginContent {...props} /> : <EditableFilePreviewPlugin {...props} />;
}

function EditableFilePreviewPlugin(props) {
    const { data } = useQuery(QUERY.ME, {
        fetchPolicy: 'network-only'
    });
    const { data: transcriptionFlagData, loading: transcriptionFlagLoading } = useQuery(GET_FEATURE_FLAG, {
        variables: { name: 'audio_transcription' },
        errorPolicy: 'all'
    });

    const { user_max_file_size } = useGetMaxFileSize();
    const { updateMessageFiles } = useS3Request();
    const { deleteFileMutation, updateFiles } = useUpdateMessageMutation();
    const downloadFile = useDownloadFile();
    const [transcribeAudio] = useMutation(MUTATION.TRANSCRIBE_AUDIO);
    const [extractVideoAudio] = useMutation(EXTRACT_VIDEO_AUDIO);
    return <FilePreviewPluginContent {...props} tools={{ data, transcriptionFlagData, transcriptionFlagLoading, user_max_file_size, updateMessageFiles, deleteFileMutation, updateFiles, downloadFile, transcribeAudio, extractVideoAudio }} />;
}

function FilePreviewPluginContent({ message, style, dropzoneFiles, hideAddButton, onClearPendingSelection, readOnly = false, canGenerate = true, onOpenVideoWorkspace, tools = {} }) {
    const {t: lnT} = useTranslation();

    let maxTypeChar = 28;
    const [imagesPreview, setImagesPreview] = useState(undefined);
    const [lightboxIsOpen, setLightboxIsOpen] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
    const [copiedToClipboard, setCopiedToClipboard] = useState(false);
    const [isTranscribingVideoAudio, setIsTranscribingVideoAudio] = useState(false);
    const [isExtractingVideoAudio, setIsExtractingVideoAudio] = useState(false);
    const [videoActionStatus, setVideoActionStatus] = useState('');
    const [showFileActionMenu, setShowFileActionMenu] = useState(false);
    const [fileActionMenuPosition, setFileActionMenuPosition] = useState(null);
    const [failedPreviewSources, setFailedPreviewSources] = useState({});
    
    const openSavedMedia = () => {
        if (onOpenVideoWorkspace && (message?.file?.cdn_url || message?.file?.url)) {
            onOpenVideoWorkspace(message, getPdfWorkspaceSource(message) || getAudioWorkspaceSource(message) || { type: message?.file?.type?.includes('video') ? 'video' : 'image', src: message?.file?.cdn_url || message?.file?.url });
        } else setLightboxIsOpen(true);
    };

    const wrapperRef = useRef(null);
    const resizeObserverRef = useRef(null);
    const inputFile = useRef(null);
    const fileActionMenuRef = useRef(null);
    const fileActionMenuButtonRef = useRef(null);

    const { data, transcriptionFlagData, transcriptionFlagLoading, user_max_file_size, updateMessageFiles, deleteFileMutation, updateFiles, downloadFile, transcribeAudio, extractVideoAudio } = tools;
    const { state: { uploadProgress } = {} } = React.useContext(AuthContext) || {};
    const transcriptionFlag = transcriptionFlagData?.getFeatureFlag;
    const transcriptionAccessDecision = evaluateFeatureFlagForUser(transcriptionFlag, data?.me, {
        loading: transcriptionFlagLoading
    });
    const shouldShowTranscribeAction = !readOnly && canGenerate && !!transcriptionFlag?.enabled;
    const requiresUpgradeForTranscription = Boolean(
        shouldShowTranscribeAction && transcriptionAccessDecision?.requiresUpgrade
    );
    const requiresTeamUpgradeForTranscription = Boolean(
        shouldShowTranscribeAction && transcriptionAccessDecision?.requiresTeamUpgrade
    );
    const audioProcessing = message?.metadata?.audioProcessing;
    const audioProcessingStatusText = getAudioProcessingStatusText(audioProcessing);

    useEffect(() => {
        if (!isAudioProcessingActive(audioProcessing)) {
            setVideoActionStatus('');
        }
    }, [audioProcessing?.status, message?.body]);

    // Setup ResizeObserver
    useEffect(() => {
        let rafId = null;
        let isObserving = true;
        
        const checkWidth = () => {
            if (!isObserving) return;
            if (wrapperRef.current) {
                const width = wrapperRef.current.getBoundingClientRect().width;
                setIsSmallScreen(width < 400);
            }
        };

        const throttledCheckWidth = () => {
            if (rafId || !isObserving) return;
            
            rafId = window.requestAnimationFrame(() => {
                checkWidth();
                rafId = null;
            });
        };

        resizeObserverRef.current = new ResizeObserver(throttledCheckWidth);
        
        if (wrapperRef.current) {
            resizeObserverRef.current.observe(wrapperRef.current);
            checkWidth();
        }

        return () => {
            isObserving = false;
            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
            }
            if (rafId) {
                window.cancelAnimationFrame(rafId);
            }
        };
    }, []);

    const getPreviews = async files => {
        let res = await getImagePreviews(files);
        if (res) {
            setImagesPreview(res);
        }
    };

    const onDropzoneFiles = async files => {
        if (readOnly) return;
        const fileListAsArray = Array.from(files);
        getPreviews(fileListAsArray);
    };

    const onChangeFile = async event => {
        if (readOnly) return;
        event.stopPropagation();
        event.preventDefault();
        const fileListAsArray = Array.from(event.target.files);
        getPreviews(fileListAsArray);
        // this.setState({file}); /// if you want to upload latter
    };

    useEffect(() => {
        if (!readOnly && dropzoneFiles) {
            onDropzoneFiles(dropzoneFiles);
        }
    }, [dropzoneFiles, readOnly]);

    useEffect(() => {
        setVideoActionStatus('');
        setIsTranscribingVideoAudio(false);
        setIsExtractingVideoAudio(false);
        setShowFileActionMenu(false);
        setFileActionMenuPosition(null);
        setFailedPreviewSources({});
    }, [message?.id]);

    useEffect(() => {
        if (!videoActionStatus) {
            return;
        }

        const timeoutId = window.setTimeout(() => {
            setVideoActionStatus('');
        }, 5000);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [videoActionStatus]);

    const updateFileActionMenuPosition = React.useCallback(() => {
        if (!fileActionMenuButtonRef.current || typeof window === 'undefined') {
            return;
        }

        const rect = fileActionMenuButtonRef.current.getBoundingClientRect();
        const estimatedMenuHeight = 180;
        const openUp = rect.bottom + estimatedMenuHeight > window.innerHeight - 8;

        setFileActionMenuPosition({
            top: openUp ? Math.max(8, rect.top - 6) : rect.bottom + 6,
            right: Math.max(8, window.innerWidth - rect.right),
            openUp
        });
    }, [lnT]);

    useEffect(() => {
        if (!showFileActionMenu) {
            return undefined;
        }

        const handleOutsideClick = event => {
            const target = event.target;
            if (fileActionMenuRef.current && fileActionMenuRef.current.contains(target)) {
                return;
            }
            if (fileActionMenuButtonRef.current && fileActionMenuButtonRef.current.contains(target)) {
                return;
            }
            setShowFileActionMenu(false);
        };

        document.addEventListener('mousedown', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [showFileActionMenu]);

    useEffect(() => {
        if (!showFileActionMenu) {
            return undefined;
        }

        updateFileActionMenuPosition();
        window.addEventListener('resize', updateFileActionMenuPosition);
        window.addEventListener('scroll', updateFileActionMenuPosition, true);

        return () => {
            window.removeEventListener('resize', updateFileActionMenuPosition);
            window.removeEventListener('scroll', updateFileActionMenuPosition, true);
        };
    }, [showFileActionMenu, updateFileActionMenuPosition]);

    const dispatchMessageUpdated = updatedMessage => {
        if (typeof window === 'undefined' || !updatedMessage?.id) {
            return;
        }

        window.dispatchEvent(
            new CustomEvent('messageUpdated', {
                detail: {
                    message: updatedMessage
                }
            })
        );
    };

    const clearFileToSend = () => {
        setImagesPreview(undefined);
        if (onClearPendingSelection) {
            onClearPendingSelection();
        }
        // inputFile.current.value = '';
    };

    const publishFiles = async index => {
        if (readOnly) return;
        let filesToSend = imagesPreview.map(previewItem => previewItem.file);
        // let imageFiles = [];
        let ordinaryFiles = [];
        filesToSend.forEach(file => {
            ordinaryFiles.push(file);
        });
        // somewhere here the successful upload
        clearFileToSend();
        if (ordinaryFiles.length) {
            const onlyFirst = ordinaryFiles[index];
            await updateMessageFiles([onlyFirst], message);
            customAnalytics.message_edit({
                "action": message.file ? "Replace" : "Add",
                "type": "File"
            }, message);
        }
    };

    const deleteFile = async () => {
        if (readOnly) return;
        // dialog would be nice here
        await deleteFileMutation(message);
    };

    const renameFile = async displayName => {
        if (readOnly) return;
        const updatedMessage = await updateFiles(message, {
            file: { ...message.file, displayName }
        });
        if (!updatedMessage) {
            throw new Error('Unable to rename the file.');
        }
        customAnalytics.message_edit({ action: 'Rename', type: 'File' }, message);
        setIsRenameModalOpen(false);
    };

    const onButtonClick = () => {
        // `current` points to the mounted file input element
        inputFile.current.click();
    };

    // NOT USED NOW
    // const removeFromTheList = file => {
    //     let previewClone = [...imagesPreview];
    //     let previewIndex = previewClone.findIndex(listFile => listFile.file === file);

    //     if (previewIndex > -1) {
    //         previewClone.splice(previewIndex, 1);
    //         if (previewClone.length > 0) {
    //             setImagesPreview(previewClone);
    //         } else {
    //             setImagesPreview(undefined);
    //         }
    //     }
    // };

    const renderViewButton = (url, type) => {
        // not working for word ATM file.type.includes('word')
        if (type === 'application/pdf') {
            return (
                <LinkTag label={lnT('folders:view')} style={{ marginRight: 8 }} href={url} target="_blank" rel="noopener noreferrer" />
            );
        }
    };

    const copyToClipboard = async url => {
        await navigator.clipboard.writeText(url);
        setCopiedToClipboard(true);
        setTimeout(() => {
            setCopiedToClipboard(false);
        }, 1500);
    };

    const handleTranscribeVideoAudio = async e => {
        e?.stopPropagation?.();

        if (!message?.id || isTranscribingVideoAudio || isExtractingVideoAudio) {
            return;
        }

        if (requiresUpgradeForTranscription || requiresTeamUpgradeForTranscription) {
            setVideoActionStatus(lnT('messages:openingUpgradeOptions'));
            openFeatureUpgradeModal({
                preset: FEATURE_UPGRADE_MODAL_PRESET.AUDIO_TRANSCRIPTION,
                upgradeType: requiresTeamUpgradeForTranscription ? 'team' : 'personal'
            });
            return;
        }

        try {
            setIsTranscribingVideoAudio(true);
            setVideoActionStatus(lnT('messages:transcriptionRunningInTheBackground'));

            const response = await transcribeAudio({
                variables: {
                    data: {
                        messageId: message.id
                    }
                }
            });

            const result = response?.data?.transcribeAudio;
            if (!result) {
                throw new Error('No transcription response');
            }

            if (result.__typename === 'TranscribeAudioError') {
                const errorMessage = result.message || lnT('messages:failedToTranscribeVideoAudio');
                const uiAction = mapTranscriptionErrorToUiAction({
                    error: result,
                    defaultDecision: transcriptionAccessDecision
                });
                if (uiAction.openUpgradeModal) {
                    setVideoActionStatus(lnT('messages:openingUpgradeOptions'));
                    openFeatureUpgradeModal({
                        preset: FEATURE_UPGRADE_MODAL_PRESET.AUDIO_TRANSCRIPTION,
                        upgradeType: 'personal'
                    });
                    return;
                }
                if (uiAction.openTeamUpgradeModal) {
                    setVideoActionStatus(lnT('messages:openingTeamUpgradeOptions'));
                    openFeatureUpgradeModal({
                        preset: FEATURE_UPGRADE_MODAL_PRESET.AUDIO_TRANSCRIPTION,
                        upgradeType: 'team'
                    });
                    return;
                }
                setVideoActionStatus(uiAction.showMessage || errorMessage);
                return;
            }

            dispatchMessageUpdated(result);
            customAnalytics.message_action(
                {
                    action: message?.file?.type?.includes('video') ? 'Transcribe Video Audio' : 'Transcribe Audio',
                    type: 'File'
                },
                message
            );
            setVideoActionStatus(
                getAudioProcessingStatusText(result?.metadata?.audioProcessing) ||
                    lnT('messages:transcriptionRunningInTheBackground')
            );
        } catch (error) {
            setVideoActionStatus(getApolloErrorMessage(error, 'Failed to transcribe audio.'));
        } finally {
            setIsTranscribingVideoAudio(false);
        }
    };

    const handleExtractVideoAudio = async e => {
        e?.stopPropagation?.();

        if (!message?.id || isTranscribingVideoAudio || isExtractingVideoAudio) {
            return;
        }

        try {
            setIsExtractingVideoAudio(true);
            setVideoActionStatus(lnT('messages:extractingAudioFromVideo'));

            const response = await extractVideoAudio({
                variables: {
                    data: {
                        messageId: message.id
                    }
                }
            });

            const extractedMessage = response?.data?.extractVideoAudio;
            if (!extractedMessage?.id) {
                throw new Error('Failed to create extracted audio message');
            }

            dispatchMessageUpdated(extractedMessage);
            customAnalytics.message_action(
                {
                    action: 'Extract Video Audio',
                    type: 'File'
                },
                message
            );
            setVideoActionStatus(lnT('messages:audioExtractedToANewMessage'));
        } catch (error) {
            const fallbackMessage =
                error?.graphQLErrors?.[0]?.message ||
                error?.message ||
                'Failed to extract audio from video.';
            setVideoActionStatus(fallbackMessage);
        } finally {
            setIsExtractingVideoAudio(false);
        }
    };

    const toggleFileActionMenu = event => {
        event.stopPropagation();
        if (showFileActionMenu) {
            setShowFileActionMenu(false);
            return;
        }

        updateFileActionMenuPosition();
        setShowFileActionMenu(true);
    };

    const renderActions = (url, type) => {
        const isVideoFile = typeof type === 'string' && type.includes('video');
        const isAudioFile = typeof type === 'string' && type.includes('audio');
        const canTranscribeAudio = isVideoFile || isAudioFile;
        const isVideoActionRunning = isTranscribingVideoAudio || isExtractingVideoAudio;
        const showTranscribeAction = canTranscribeAudio && !isAudioFile && shouldShowTranscribeAction;
        const replaceLabel =
            uploadProgress && uploadProgress[message.id] && uploadProgress[message.id].type === 'file'
                ? lnT('messages:updating', { value1: String(parseInt(uploadProgress[message.id].percentage)) })
                : lnT('messages:replace');

        const renderFileActionMenuPortal = () => {
            if (!showFileActionMenu || !fileActionMenuPosition || typeof document === 'undefined') {
                return null;
            }

            return ReactDOM.createPortal(
                <FileActionMenuPortal
                    ref={fileActionMenuRef}
                    $top={fileActionMenuPosition.top}
                    $right={fileActionMenuPosition.right}
                    $openUp={fileActionMenuPosition.openUp}
                    onClick={event => event.stopPropagation()}
                >
                    {!readOnly ? <>
                    <FileActionMenuItem
                        onClick={event => {
                            event.stopPropagation();
                            setShowFileActionMenu(false);
                            setIsRenameModalOpen(true);
                        }}
                    >
                        <FileActionMenuItemIcon>
                            <FiEdit2 size={15} />
                        </FileActionMenuItemIcon>{lnT('folders:rename')}</FileActionMenuItem>
                    </> : null}
                    <FileActionMenuItem
                        onClick={event => {
                            event.stopPropagation();
                            setShowFileActionMenu(false);
                            if (getPdfWorkspaceSource(message) && onOpenVideoWorkspace) openSavedMedia();
                            else window.open(url, '_blank');
                        }}
                    >
                        <FileActionMenuItemIcon>
                            <IoMdOpen size={15} />
                        </FileActionMenuItemIcon>{lnT('common:open')}</FileActionMenuItem>
                    <FileActionMenuItem
                        onClick={event => {
                            event.stopPropagation();
                            setShowFileActionMenu(false);
                            if (readOnly) FileSaver.saveAs(url, message.file.displayName || message.file.name);
                            else downloadFile(message.file.name, message.file.size, message.file.displayName);
                        }}
                    >
                        <FileActionMenuItemIcon>
                            <IoMdDownload size={15} />
                        </FileActionMenuItemIcon>{lnT('messages:download')}</FileActionMenuItem>
                    {!readOnly ? <>
                    <FileActionMenuItem
                        onClick={event => {
                            event.stopPropagation();
                            setShowFileActionMenu(false);
                            onButtonClick();
                        }}
                    >
                        <FileActionMenuItemIcon>
                            <IoIosDocument size={15} />
                        </FileActionMenuItemIcon>
                        {replaceLabel}
                    </FileActionMenuItem>
                    <FileActionMenuItem
                        destructive
                        onClick={event => {
                            event.stopPropagation();
                            setShowFileActionMenu(false);
                            setIsDeleteModalOpen(true);
                        }}
                    >
                        <FileActionMenuItemIcon destructive>
                            <IoMdTrash size={15} />
                        </FileActionMenuItemIcon>{lnT('common:remove')}</FileActionMenuItem>

                    </> : null}
                    {showTranscribeAction ? (
                        <React.Fragment>
                            <FileActionMenuDivider />
                            <FileActionMenuItem
                                disabled={isVideoActionRunning}
                                onClick={event => {
                                    event.stopPropagation();
                                    setShowFileActionMenu(false);
                                    if (requiresUpgradeForTranscription || requiresTeamUpgradeForTranscription) {
                                        setVideoActionStatus(lnT('messages:openingUpgradeOptions'));
                                        openFeatureUpgradeModal({
                                            preset: FEATURE_UPGRADE_MODAL_PRESET.AUDIO_TRANSCRIPTION,
                                            upgradeType: requiresTeamUpgradeForTranscription ? 'team' : 'personal'
                                        });
                                        return;
                                    }
                                    handleTranscribeVideoAudio();
                                }}
                            >
                                <FileActionMenuItemIcon>
                                    <IoMdMic size={15} />
                                </FileActionMenuItemIcon>
                                {isTranscribingVideoAudio
                                    ? lnT('messages:transcribing')
                                    : (requiresUpgradeForTranscription || requiresTeamUpgradeForTranscription)
                                        ? lnT('messages:transcribeAudioUpgrade')
                                        : lnT('messages:transcribeAudio')}
                            </FileActionMenuItem>
                            {isVideoFile ? (
                                <FileActionMenuItem
                                    disabled={isVideoActionRunning}
                                    onClick={event => {
                                        event.stopPropagation();
                                        setShowFileActionMenu(false);
                                        handleExtractVideoAudio();
                                    }}
                                >
                                    <FileActionMenuItemIcon>
                                        <IoMdDownload size={15} />
                                    </FileActionMenuItemIcon>
                                    {isExtractingVideoAudio ? lnT('messages:extracting') : lnT('messages:extractAudio')}
                                </FileActionMenuItem>
                            ) : null}
                        </React.Fragment>
                    ) : null}
                </FileActionMenuPortal>,
                document.body
            );
        };

        return (
            <React.Fragment>
                {renderFileActionMenuPortal()}
                <DeleteModal
                    title={lnT('messages:deleteFile_37c5b893')}
                    description={lnT('messages:youWillNotBeAbleToGet')}
                    isOpen={isDeleteModalOpen}
                    closeModal={response => {
                        if (response) {
                            deleteFile();
                        }
                        setIsDeleteModalOpen(false);
                    }}
                ></DeleteModal>
                <RenameFileModal
                    isOpen={isRenameModalOpen}
                    media={message.file}
                    onClose={() => setIsRenameModalOpen(false)}
                    onSubmit={renameFile}
                />
                {audioProcessingStatusText || videoActionStatus ? (
                    <div
                        style={{
                            marginTop: 2,
                            color: 'rgba(255,255,255,0.7)',
                            fontSize: 12
                        }}
                    >
                        {audioProcessingStatusText || videoActionStatus}
                    </div>
                ) : null}
            </React.Fragment>
        );
    };

    const renderPublishAction = (index, size) => {
        if (data && data.me) {
            // if (data.me.role === USER_ROLE.ADMIN) {
            //     return (
            //         <ActionTag
            //             label={message.file ? 'Replace' : 'Publish'}
            //             style={{ backgroundColor: GREEN, marginRight: 8 }}
            //             onClick={() => publishFiles(index)}
            //         />
            //     );
            // } else if (data.me.planType === 'pro') {
            //     return (
            //         <ActionTag
            //             label={message.file ? 'Replace' : 'Publish'}
            //             style={{ backgroundColor: GREEN, marginRight: 8 }}
            //             onClick={() => publishFiles(index)}
            //         />
            //     );
            // } else if (data.me.planType === USER_TYPE.PERSONAL && size > user_max_file_size) {
            //     return (
            //         <InactiveTag
            //             label="File too large"
            //             style={{
            //                 marginRight: 8
            //             }}
            //         />
            //     );
            // } else
            if (size > user_max_file_size) {
                // TODO PLAN
                return (
                    <InactiveTag
                        label={lnT('messages:fileTooLarge')}
                        style={{
                            marginRight: 8
                        }}
                    />
                );
            } else {
                return (
                    <ActionTag
                        label={message.file ? lnT('messages:replace') : lnT('messages:publish')}
                        style={{ backgroundColor: GREEN, marginRight: 8 }}
                        onClick={e => {
                            e.stopPropagation();
                            publishFiles(index);
                        }}
                    />
                );
            }
        }
    };

    const renderPublishActions = (index, size) => {
        return (
            <div style={{ display: 'flex' }}>
                {/* <div style={{ flex: 1 }}></div> */}
                {renderPublishAction(index, size)}
                <ActionTag
                    label={lnT('common:cancel')}
                    onClick={e => {
                        e.stopPropagation();
                        clearFileToSend();
                    }}
                ></ActionTag>
            </div>
        );
    };

    const fileName = (url, name, displayName) => {
        let title;
        if (displayName) {
            title = displayName;
        } else if (name) {
            title = name;
        } else {
            // in case that we haven't saved the name of the file
            title = url ? url.substr(url.lastIndexOf('/') + 1) : lnT('messages:file');
        }
        return decodeDisplayText(title);
    };

    const getFormat = name => {
        return String(name || '').split('.').pop();
    };

    const markPreviewSourceFailed = source => {
        if (!source) {
            return;
        }

        setFailedPreviewSources(previous => {
            if (previous[source]) {
                return previous;
            }

            return {
                ...previous,
                [source]: true
            };
        });
    };

    const getPreviewSources = (url, type = '', format = '', file = null) => {
        const normalizedType = String(type || '').toLowerCase();
        const normalizedFormat = String(format || '').toLowerCase();
        const isHeic = normalizedType.includes('image/heic') || normalizedFormat.includes('heic');

        if (normalizedType.includes('image') && !isHeic) {
            return getUniqueSources([
                file?.thumbnail?.lg,
                file?.thumbnail?.md,
                file?.thumbnail?.sm,
                file?.cdn_url,
                url
            ]);
        }

        const thumbnailSources = getThumbnailSources(file?.thumbnail);
        const isPlayableMedia = normalizedType.includes('audio') || normalizedType.includes('video');

        if (isPdfDocument(normalizedType, normalizedFormat, file?.mime) || (!isPlayableMedia && thumbnailSources.length > 0)) {
            return getUniqueSources(thumbnailSources);
        }

        return [];
    };

    const renderPreviewImage = (sources, alt, maxWidth = '200px') => {
        const usableSources = sources.filter(source => !failedPreviewSources[source]);
        const imageUrl = usableSources[0];

        if (!imageUrl) {
            return null;
        }

        return (
            <div
                style={{
                    borderRadius: 8,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.04)',
                    width: '100%',
                    overflow: 'hidden'
                }}
                onClick={e => {
                    e.stopPropagation();
                    openSavedMedia();
                }}
            >
                <img
                    src={imageUrl}
                    alt={alt}
                    style={{
                        width: '100%',
                        maxWidth: isSmallScreen ? '100%' : maxWidth,
                        objectFit: 'contain',
                        borderRadius: 8,
                        cursor: 'zoom-in'
                    }}
                    onError={event => {
                        const failedSource = event.currentTarget.currentSrc || event.currentTarget.src || imageUrl;
                        markPreviewSourceFailed(failedSource);
                        if (failedSource !== imageUrl) {
                            markPreviewSourceFailed(imageUrl);
                        }

                        const normalizedFailedSourceIndex = usableSources.indexOf(failedSource);
                        const failedSourceIndex =
                            normalizedFailedSourceIndex > -1
                                ? normalizedFailedSourceIndex
                                : usableSources.indexOf(imageUrl);
                        const nextSource =
                            failedSourceIndex > -1
                                ? usableSources[failedSourceIndex + 1]
                                : usableSources.find(source => source !== imageUrl);
                        if (nextSource) {
                            event.target.src = nextSource;
                            return;
                        }

                        event.target.style.display = 'none';
                    }}
                />
            </div>
        );
    };

    const renderFileType = (url, type = '', format, file) => {
        const normalizedType = String(type || '').toLowerCase();
        const normalizedFormat = String(format || '').toLowerCase();

        if (normalizedType.includes('image')) {
            const isHeic = normalizedType.includes('image/heic') || normalizedFormat.includes('heic');
            // Only render image preview if we have a valid URL and it's not HEIC
            if (!isHeic && url) {
                return renderPreviewImage(
                    getPreviewSources(url, normalizedType, normalizedFormat, file),
                    fileName(url, file?.name, file?.displayName) || 'Image'
                );
            } else {
                // No URL available (large file) or HEIC - show icon instead
                return <ImageIcon />;
            }
        }

        const documentPreview = renderPreviewImage(
            getPreviewSources(url, normalizedType, normalizedFormat, file),
            `Preview of ${fileName(url, file?.name, file?.displayName) || 'document'}`,
            '220px'
        );

        if (documentPreview) {
            return documentPreview;
        }

        return (
            <FileIcon
                type={type}
                format={format}
                mime={file?.mime}
                name={fileName(url, file?.name, file?.displayName)}
            />
        );
    };

    const hasRenderablePreview = (url, type = '', format = '', file = null) => {
        const normalizedType = String(type || '').toLowerCase();
        const normalizedFormat = String(format || '').toLowerCase();
        const isHeic = normalizedType.includes('image/heic') || normalizedFormat.includes('heic');

        if (normalizedType.includes('image')) {
            return Boolean(url) && !isHeic;
        }

        return getPreviewSources(url, normalizedType, normalizedFormat, file).some(
            source => !failedPreviewSources[source]
        );
    };

    const getPreviewCarouselItem = (file, url, type, format) => {
        const previewSources = getPreviewSources(url, type, format, file);

        if (previewSources.length === 0) {
            return file;
        }

        return {
            ...file,
            src: previewSources[0],
            sourceChain: previewSources,
            fileName: file?.displayName || file?.name || null
        };
    };

    const renderContent = (url, name, type = '', size, displayName, format, file = null, allowActionMenu = false) => {
        const isImagePreview = hasRenderablePreview(url, type, format, file);
        
        return (
            <div style={{ 
                display: 'flex',
                marginBottom: readOnly ? 0 : 8,
                position: 'relative',
                paddingRight: allowActionMenu ? 36 : 0,
                flexDirection: (isSmallScreen && isImagePreview) ? 'column' : 'row',
                alignItems: (isSmallScreen && isImagePreview) ? 'stretch' : 'flex-start',
                gap: 8,
                minWidth: 0
            }}>
                <div style={{ 
                    flexShrink: 0,
                    display: 'flex',
                    width: (isSmallScreen && isImagePreview) ? '100%' : 'auto'
                }}>
                    {renderFileType(url, type, format, file)}
                </div>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: (isSmallScreen && isImagePreview) ? 'none' : 1,
                    minWidth: 0,
                    width: (isSmallScreen && isImagePreview) ? '100%' : 'auto'
                }}>
                    <div style={{
                        marginBottom: 4,
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 8,
                        minWidth: 0
                    }}>
                        {url ? (
                            <FileNameLink
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={lnT('auth:open', { value1: String(fileName(url, name, displayName)) })}
                                data-message-interactive="true"
                                onClick={event => {
                                    event.stopPropagation();
                                    if (getPdfWorkspaceSource(message) && onOpenVideoWorkspace) {
                                        event.preventDefault();
                                        openSavedMedia();
                                    }
                                }}
                            >
                                {fileName(url, name, displayName)}
                            </FileNameLink>
                        ) : (
                            <FileNameText>{fileName(url, name, displayName)}</FileNameText>
                        )}
                        {allowActionMenu ? (
                            <FileActionMenuButton
                                ref={fileActionMenuButtonRef}
                                type="button"
                                $compact
                                style={{ position: 'absolute', top: 0, right: 0 }}
                                aria-label={lnT('messages:openFileActions')}
                                onClick={toggleFileActionMenu}
                            >
                                <FiMoreVertical size={16} color="rgba(255,255,255,.8)" />
                            </FileActionMenuButton>
                        ) : null}
                    </div>
                    <div style={{ marginBottom: 4, color: 'rgba(255,255,255,0.65)' }}>
                        {size ? prettySize(size) : null}
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.65)' }}>{lnT('messages:type')}{getFormat(name)}
                    </div>
                </div>
            </div>
        );
    };

    const renderCarousel = item => {
        return (
            <CarouselModal
                readOnly={readOnly}
                allowExtraction={!readOnly && canGenerate}
                isOpen={lightboxIsOpen}
                closeModal={e => {
                    e.stopPropagation();
                    setLightboxIsOpen(false);
                }}
                items={[item]}
                fileType="File"
                currentMessage={message}
                messageId={message?.id}
            ></CarouselModal>
        );
    };

    const renderInlineVideoPreview = (url, type) => {
        if (!url || !type || !type.includes('video')) {
            return null;
        }

        return (
            <div
                style={{
                    position: 'relative',
                    width: '100%',
                    borderRadius: 8,
                    overflow: 'hidden',
                    marginBottom: 8,
                    backgroundColor: 'rgba(255, 255, 255, 0.04)'
                }}
            >
                <ReactPlayer
                    url={url}
                    width="100%"
                    controls={true}
                    pip={true}
                    style={{
                        display: 'flex',
                        maxHeight: 300,
                        minHeight: 100,
                        overflow: 'hidden',
                        borderRadius: 8
                    }}
                />
            </div>
        );
    };

    if (imagesPreview) {
        return imagesPreview.map(({ reader, file }, index) => {
            const { name, size, type, displayName, format } = file;
            const url = reader?.result || null;

            return (
                <FileWrapper ref={wrapperRef} key={index} style={{ ...style }}>
                    {renderContent(url, name, type, size, displayName, format, null)}
                    {renderPublishActions(index, size)}
                </FileWrapper>
            );
        });
    } else if (message.file) {
        const { url, name, displayName, type, size, format, cdn_url } = message.file;
        // Prefer CDN URL for actions/display, fallback to S3 signed URL
        const fileUrl = cdn_url || url;
        const isAudioFile = typeof type === 'string' && type.includes('audio');

        if (isAudioFile) {
            return (
                <React.Fragment>
                    <AudioPlayer
                        readOnly={readOnly}
                        canGenerate={canGenerate}
                        url={fileUrl}
                        fallbackUrl={cdn_url ? url : null}
                        fileName={name}
                        title={decodeDisplayText(displayName || name)}
                        initialDuration={message.file.duration}
                        message={message}
                        style={{ ...style }}
                        attachmentActions={
                            <FileActionMenuButton
                                ref={fileActionMenuButtonRef}
                                type="button"
                                $compact
                                aria-label={lnT('messages:openFileActions')}
                                onClick={toggleFileActionMenu}
                            >
                                <FiMoreVertical size={16} color="rgba(255,255,255,.8)" />
                            </FileActionMenuButton>
                        }
                    />
                    {renderActions(fileUrl, type)}
                    <input type="file" id="file" ref={inputFile} style={{ display: 'none' }} onChange={onChangeFile} />
                </React.Fragment>
            );
        }

        return (
            <FileWrapper ref={wrapperRef} style={{ ...style }}>
                {renderInlineVideoPreview(fileUrl, type)}
                {renderContent(fileUrl, name, type, size, displayName, format, message.file, true)}
                {renderActions(fileUrl, type)}
                <input type="file" id="file" ref={inputFile} style={{ display: 'none' }} onChange={onChangeFile} />
                {renderCarousel(getPreviewCarouselItem(message.file, fileUrl, type, format))}
            </FileWrapper>
        );
    } else {
        if (readOnly || hideAddButton) {
            return null;
        }
        return (
            <AddFileWrapper
                ref={wrapperRef}
                style={{ ...style }}
                uploading={uploadProgress && uploadProgress[message.id] && uploadProgress[message.id].type === 'file'}
                onClick={() => onButtonClick()}
            >
                <div style={{ color: 'white' }}>
                    {uploadProgress && uploadProgress[message.id] && uploadProgress[message.id].type === 'file'
                        ? lnT('messages:uploading_72cb29c9')
                        : lnT('messages:attach')}
                </div>
                <input type="file" id="file" ref={inputFile} style={{ display: 'none' }} onChange={onChangeFile} />
            </AddFileWrapper>
        );
    }
}

const FileWrapper = styled.div`
    background-color: rgba(255, 255, 255, 0.03);
    border: 1px solid ${PALE_WHITE_1};
    border-radius: 12px;
    padding: 8px;
    width: 100%;
    overflow: hidden;
`;

const AddFileWrapper = styled.div`
    border-radius: 8px;
    border: 1px solid ${PALE_WHITE_1};
    background-color: ${props => (props.uploading ? GREEN : 'rgba(255, 255, 255, 0.05)')};
    cursor: pointer;
    padding: 8px 12px;
    display: flex;
    justify-content: center;
    width: 100%;
    &:hover {
        background-color: ${props => (props.uploading ? GREEN : 'rgba(255, 255, 255, 0.1)')};
    }
`;

const FileActionMenuButton = styled.button`
    border: ${props => (props.$compact ? 'none' : `1px solid ${PALE_WHITE_1}`)};
    border-radius: ${props => (props.$compact ? '8px' : '10px')};
    background: ${props => (props.$compact ? 'transparent' : 'rgba(0, 0, 0, 0.28)')};
    color: rgba(255, 255, 255, 0.82);
    height: 32px;
    width: ${props => (props.$compact ? '28px' : '36px')};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
    opacity: ${props => (props.disabled ? 0.6 : 1)};
    transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;

    &:hover {
        background: ${props => (props.$compact ? PALE_WHITE_1 : 'rgba(0, 0, 0, 0.42)')};
        border-color: ${props => (props.$compact ? 'transparent' : 'rgba(255, 255, 255, 0.16)')};
        color: rgba(255, 255, 255, 0.92);
    }
`;

const FileNameText = styled.div`
    word-break: break-word;
    color: rgba(255, 255, 255, 0.8);
    font-weight: 500;
    line-height: 20px;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
    min-width: 0;
`;

const FileNameLink = styled.a`
    word-break: break-word;
    color: rgba(255, 255, 255, 0.8);
    font-weight: 500;
    line-height: 20px;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
    min-width: 0;
    text-decoration: none;
    cursor: pointer;

    &&,
    &&:link,
    &&:visited,
    &&:active {
        color: rgba(255, 255, 255, 0.85) !important;
    }

    &&:hover {
        color: rgba(255, 255, 255, 0.85) !important;
        text-decoration: underline;
        text-underline-offset: 2px;
    }

    &:focus-visible {
        outline: 2px solid rgba(255, 255, 255, 0.72);
        outline-offset: 2px;
    }
`;

const FileActionMenuPortal = styled.div`
    position: fixed;
    top: ${props => `${props.$top}px`};
    right: ${props => `${props.$right}px`};
    transform: ${props => (props.$openUp ? 'translateY(-100%)' : 'none')};
    min-width: 196px;
    background: rgba(20, 24, 28, 0.96);
    color: rgba(255, 255, 255, 0.88);
    z-index: 3000;
    box-shadow: 0 18px 40px rgba(0, 0, 0, 0.28);
    border: 1px solid ${PALE_WHITE_1};
    border-radius: 14px;
    overflow: hidden;
    backdrop-filter: saturate(180%) blur(24px);
    -webkit-backdrop-filter: saturate(180%) blur(24px);
`;

const FileActionMenuDivider = styled.div`
    height: 1px;
    background-color: rgba(255, 255, 255, 0.1);
    margin: 2px 0;
`;

const FileActionMenuItem = styled.button`
    width: 100%;
    border: none;
    background: transparent;
    color: ${props => (props.destructive ? '#ff7a7a' : 'rgba(255, 255, 255, 0.88)')};
    text-align: left;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    font-size: 13px;
    line-height: 1.2;
    font-weight: 500;
    font-family: inherit;
    cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
    opacity: ${props => (props.disabled ? 0.5 : 1)};
    transition: background-color 0.12s ease, color 0.12s ease;

    &:hover {
        background-color: rgba(255, 255, 255, 0.08);
    }
`;

const FileActionMenuItemIcon = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    color: ${props => (props.destructive ? '#ff7a7a' : 'rgba(255, 255, 255, 0.78)')};
`;

export default FilePreviewPlugin;
